/* tslint:disable */
/* eslint-disable */
import * as runtime from '../runtime';
import type { WarehouseInvoice, UnpaidInvoiceSummary } from '../models/index';
import {
    WarehouseInvoiceFromJSON,
    WarehouseInvoiceToJSON,
} from '../models/index';

export interface WarehouseInvoiceGenerateInvoiceRequest {
    warehouseCourierId: number;
    manifestId: number;
    subCourierId?: number;
    /** Generate for a single non-partner-linked courier (CourierUser id). Mutually exclusive with subCourierId. */
    subCourierUserId?: number;
    /** When false, sea-freight packages are excluded from the bill entirely. Defaults to included. */
    includeSeaPackages?: boolean;
}

export interface WarehouseInvoiceBulkGenerateInvoicesRequest {
    warehouseCourierId: number;
    manifestId: number;
    /** When false, sea-freight packages are excluded from the bill entirely. Defaults to included. */
    includeSeaPackages?: boolean;
}

/** A billed party the run could not invoice, and why. */
export interface WarehouseInvoiceGenerationFailure {
    party: {
        kind: 'partner' | 'courierUser' | 'unassigned';
        id?: number;
        label?: string;
    };
    /** Machine-readable cause, e.g. NO_DEFAULT_PRICING_PLAN. */
    code: string;
    message: string;
    /**
     * Which packages the failure is about. Without these, "Unassigned packages
     * (1) — no default pricing plan" means hunting a whole manifest for the
     * one package that broke the run.
     */
    details?: {
        packageCount?: number;
        packageIds?: Array<number>;
        trackingNumbers?: Array<string>;
        [key: string]: any;
    };
}

/** A billed party that already has an invoice for this manifest. */
export interface WarehouseInvoiceAlreadyBilled {
    party: {
        kind: 'partner' | 'courierUser' | 'unassigned';
        id?: number;
        label?: string;
    };
    invoiceId?: number;
}

/**
 * Bulk generation reports per-party outcomes rather than throwing: the
 * invoices it created, the parties it could not bill and why, and the ones
 * that were already billed (re-running a billed manifest is routine — the next
 * step there is a regenerate, not a fix).
 */
export interface WarehouseInvoiceBulkGenerateResult {
    invoices: Array<WarehouseInvoice>;
    failures: Array<WarehouseInvoiceGenerationFailure>;
    alreadyBilled: Array<WarehouseInvoiceAlreadyBilled>;
}

export interface WarehouseInvoiceBulkRegenerateRequest {
    warehouseCourierId: number;
    manifestId: number;
    /** Defaults to 'ready' on the server. */
    scope?: WarehouseRegenerateScope;
}

/** An invoice a bulk regeneration could not reprice, or deliberately did not. */
export interface WarehouseInvoiceRegenerateFailure {
    invoiceId: number;
    invoiceNumber?: string;
    code: string;
    message: string;
}

export interface WarehouseInvoiceRegenerateSkipped {
    invoiceId: number;
    invoiceNumber?: string;
    reason: string;
}

export interface WarehouseInvoiceBulkRegenerateResult {
    regenerated: Array<WarehouseInvoice>;
    failures: Array<WarehouseInvoiceRegenerateFailure>;
    /** Paid invoices — never repriced in place. */
    skipped: Array<WarehouseInvoiceRegenerateSkipped>;
    scope: WarehouseRegenerateScope;
}

export interface WarehouseInvoiceGetInvoicesRequest {
    warehouseCourierId: number;
    filters?: {
        subCourierId?: number;
        unassigned?: boolean;
        status?: string;
        manifestId?: number;
        dateFrom?: string;
        dateTo?: string;
        limit?: number;
        skip?: number;
    };
}

export interface WarehouseInvoiceAssignInvoiceRequest {
    invoiceId: number;
    /** Partner-linked sub-courier (Courier id). Supply this OR subCourierUserId, never both. */
    subCourierId?: number;
    /** Non-partner-linked courier (CourierUser id in the warehouse realm). Supply this OR subCourierId, never both. */
    subCourierUserId?: number;
}

export interface WarehouseInvoiceGetInvoiceRequest {
    invoiceId: number;
}

export interface WarehouseInvoiceDeleteInvoiceRequest {
    invoiceId: number;
}

export interface WarehouseInvoiceGetInvoiceCreditRequest {
    invoiceId: number;
}

export interface WarehouseInvoiceApplyCreditRequest {
    invoiceId: number;
    /** Amount of store credit to apply; must not exceed the balance or what is still due. */
    amount: number;
}

/** The billed courier's store credit with the issuing warehouse, against one invoice. */
export interface WarehouseInvoiceCredit {
    /** Warehouse-realm CourierUser whose RewardsAccount holds the credit; null when none resolves. */
    courierUserId: number | null;
    rewardsAccountId: number | null;
    creditBalance: number;
    creditApplied: number;
    amountDue: number;
}

export interface WarehouseInvoiceUpdateInvoiceStatusRequest {
    invoiceId: number;
    status: string;
    paymentMethod?: string;
    paymentReference?: string;
}

export interface WarehouseInvoiceSendInvoiceEmailRequest {
    invoiceId: number;
    isResend?: boolean;
}

export interface WarehouseInvoiceGetMyInvoicesRequest {
    subCourierId: number;
    filters?: {
        status?: string;
        manifestId?: number;
        limit?: number;
        skip?: number;
    };
}

export interface WarehouseInvoiceGetUnpaidInvoiceSummaryRequest {
    subCourierId: number;
}

export interface WarehouseInvoiceSendUnpaidRemindersRequest {
    warehouseCourierId: number;
    /** Preview only. The server treats anything but an explicit false as a dry run. */
    dryRun?: boolean;
}

export type WarehouseInvoiceReminderChannel = 'whatsapp' | 'email';

/** One owner who would be (or was) reminded about a warehouse's unpaid bills. */
export interface WarehouseInvoiceReminderPreview {
    warehouseCourierId: number;
    warehouseName: string;
    kind: 'partner' | 'courierUser';
    billedId: number;
    billedName: string;
    /** Null when this channel is skipped for this owner (see `skipped`). */
    email: string | null;
    whatsapp: string | null;
    invoiceCount: number;
    invoiceIds: number[];
    /** Amount due per currency, e.g. { USD: 120.5 }. */
    totals: Record<string, number>;
    message: string;
}

export interface WarehouseInvoiceReminderSkip {
    warehouseCourierId: number;
    billedName: string;
    channel: WarehouseInvoiceReminderChannel;
    /** missing_owner | missing_email | missing_whatsapp | whatsapp_opted_out | ox_line_number */
    reason: string;
}

export interface WarehouseInvoiceReminderResult {
    dryRun: boolean;
    groups: number;
    sent: Record<WarehouseInvoiceReminderChannel, number>;
    failed: Record<WarehouseInvoiceReminderChannel, number>;
    /** Already reminded today on that channel (once-a-day limit). */
    duplicates: Record<WarehouseInvoiceReminderChannel, number>;
    skipped: WarehouseInvoiceReminderSkip[];
    preview: WarehouseInvoiceReminderPreview[];
    error?: string;
}

/**
 * What a regeneration run charges for.
 *
 * 'ready'    — only packages scanned Ready (the historical default).
 * 'manifest' — every package on the manifest, the same basis first generation
 *              used. Needed by warehouses whose sub-couriers never scan
 *              packages to Ready and would otherwise regenerate a $0 bill.
 */
export type WarehouseRegenerateScope = 'ready' | 'manifest';

export interface WarehouseInvoiceRegenerateInvoiceRequest {
    invoiceId: number;
    /** Defaults to 'ready' on the server. */
    scope?: WarehouseRegenerateScope;
}

export interface WarehouseInvoiceRepairTotalsRequest {
    /**
     * Defaults to true on the server — a call with no options reports and
     * changes nothing. Pass false to write the corrected totals.
     */
    dryRun?: boolean;
    /** Limit the scan to one warehouse. */
    warehouseCourierId?: number;
    /** Limit the scan to specific invoices. */
    invoiceIds?: Array<number>;
}

export interface WarehouseInvoiceTotalsRepairEntry {
    invoiceId: number;
    invoiceNumber?: string;
    status?: string;
    changedFields: Array<string>;
    storedSubtotal: number;
    correctSubtotal: number;
    storedGrandTotal: number;
    correctGrandTotal: number;
    delta: number;
}

export interface WarehouseInvoiceRepairTotalsResult {
    dryRun: boolean;
    scanned: number;
    mismatched: Array<WarehouseInvoiceTotalsRepairEntry>;
    repaired: number;
    totalDelta: number;
    /** Paid invoices are reported, never modified. */
    requiresManualReview: Array<WarehouseInvoiceTotalsRepairEntry>;
    failed: Array<{ invoiceId: number; invoiceNumber?: string; error: string }>;
}

export class WarehouseInvoiceApi extends runtime.BaseAPI {

    async warehouseInvoiceGenerateInvoiceRaw(requestParameters: WarehouseInvoiceGenerateInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<object>> {
        if (requestParameters['warehouseCourierId'] == null) {
            throw new runtime.RequiredError('warehouseCourierId', 'Required parameter "warehouseCourierId" was null or undefined when calling warehouseInvoiceGenerateInvoice().');
        }
        if (requestParameters['manifestId'] == null) {
            throw new runtime.RequiredError('manifestId', 'Required parameter "manifestId" was null or undefined when calling warehouseInvoiceGenerateInvoice().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const body: any = {
            warehouseCourierId: requestParameters['warehouseCourierId'],
            manifestId: requestParameters['manifestId'],
        };
        if (requestParameters['subCourierId'] != null) {
            body.subCourierId = requestParameters['subCourierId'];
        }
        if (requestParameters['subCourierUserId'] != null) {
            body.subCourierUserId = requestParameters['subCourierUserId'];
        }
        if (requestParameters['includeSeaPackages'] != null) {
            body.includeSeaPackages = requestParameters['includeSeaPackages'];
        }

        const response = await this.request({
            path: `/WarehouseInvoices/generate`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    async warehouseInvoiceGenerateInvoice(requestParameters: WarehouseInvoiceGenerateInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<object> {
        const response = await this.warehouseInvoiceGenerateInvoiceRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceBulkGenerateInvoicesRaw(requestParameters: WarehouseInvoiceBulkGenerateInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoiceBulkGenerateResult>> {
        if (requestParameters['warehouseCourierId'] == null) {
            throw new runtime.RequiredError('warehouseCourierId', 'Required parameter "warehouseCourierId" was null or undefined when calling warehouseInvoiceBulkGenerateInvoices().');
        }
        if (requestParameters['manifestId'] == null) {
            throw new runtime.RequiredError('manifestId', 'Required parameter "manifestId" was null or undefined when calling warehouseInvoiceBulkGenerateInvoices().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const bulkBody: any = {
            warehouseCourierId: requestParameters['warehouseCourierId'],
            manifestId: requestParameters['manifestId'],
        };
        if (requestParameters['includeSeaPackages'] != null) {
            bulkBody.includeSeaPackages = requestParameters['includeSeaPackages'];
        }

        const response = await this.request({
            path: `/WarehouseInvoices/bulk-generate`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: bulkBody,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    async warehouseInvoiceBulkGenerateInvoices(requestParameters: WarehouseInvoiceBulkGenerateInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoiceBulkGenerateResult> {
        const response = await this.warehouseInvoiceBulkGenerateInvoicesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceBulkRegenerateInvoicesRaw(requestParameters: WarehouseInvoiceBulkRegenerateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoiceBulkRegenerateResult>> {
        if (requestParameters['warehouseCourierId'] == null) {
            throw new runtime.RequiredError('warehouseCourierId', 'Required parameter "warehouseCourierId" was null or undefined when calling warehouseInvoiceBulkRegenerateInvoices().');
        }
        if (requestParameters['manifestId'] == null) {
            throw new runtime.RequiredError('manifestId', 'Required parameter "manifestId" was null or undefined when calling warehouseInvoiceBulkRegenerateInvoices().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/bulk-regenerate`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: {
                warehouseCourierId: requestParameters['warehouseCourierId'],
                manifestId: requestParameters['manifestId'],
                ...(requestParameters['scope'] != null ? { scope: requestParameters['scope'] } : {}),
            },
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    /**
     * Reprice every invoice already raised against a manifest, on one basis.
     */
    async warehouseInvoiceBulkRegenerateInvoices(requestParameters: WarehouseInvoiceBulkRegenerateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoiceBulkRegenerateResult> {
        const response = await this.warehouseInvoiceBulkRegenerateInvoicesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceGetInvoicesRaw(requestParameters: WarehouseInvoiceGetInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<WarehouseInvoice>>> {
        if (requestParameters['warehouseCourierId'] == null) {
            throw new runtime.RequiredError('warehouseCourierId', 'Required parameter "warehouseCourierId" was null or undefined when calling warehouseInvoiceGetInvoices().');
        }

        const queryParameters: any = {};
        if (requestParameters['filters'] != null) {
            queryParameters['filters'] = JSON.stringify(requestParameters['filters']);
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/WarehouseInvoices/invoices/${encodeURIComponent(String(requestParameters['warehouseCourierId']))}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(WarehouseInvoiceFromJSON));
    }

    async warehouseInvoiceGetInvoices(requestParameters: WarehouseInvoiceGetInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<WarehouseInvoice>> {
        const response = await this.warehouseInvoiceGetInvoicesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceGetInvoiceRaw(requestParameters: WarehouseInvoiceGetInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoice>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceGetInvoice().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WarehouseInvoiceFromJSON(jsonValue));
    }

    async warehouseInvoiceGetInvoice(requestParameters: WarehouseInvoiceGetInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoice> {
        const response = await this.warehouseInvoiceGetInvoiceRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceUpdateInvoiceStatusRaw(requestParameters: WarehouseInvoiceUpdateInvoiceStatusRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoice>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceUpdateInvoiceStatus().');
        }
        if (requestParameters['status'] == null) {
            throw new runtime.RequiredError('status', 'Required parameter "status" was null or undefined when calling warehouseInvoiceUpdateInvoiceStatus().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const body: any = {
            invoiceId: requestParameters['invoiceId'],
            status: requestParameters['status'],
        };
        if (requestParameters['paymentMethod'] != null) {
            body.paymentMethod = requestParameters['paymentMethod'];
        }
        if (requestParameters['paymentReference'] != null) {
            body.paymentReference = requestParameters['paymentReference'];
        }

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/status`,
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WarehouseInvoiceFromJSON(jsonValue));
    }

    async warehouseInvoiceUpdateInvoiceStatus(requestParameters: WarehouseInvoiceUpdateInvoiceStatusRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoice> {
        const response = await this.warehouseInvoiceUpdateInvoiceStatusRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceDeleteInvoiceRaw(requestParameters: WarehouseInvoiceDeleteInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<object>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceDeleteInvoice().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}`,
            method: 'DELETE',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    async warehouseInvoiceDeleteInvoice(requestParameters: WarehouseInvoiceDeleteInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<object> {
        const response = await this.warehouseInvoiceDeleteInvoiceRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceAssignInvoiceRaw(requestParameters: WarehouseInvoiceAssignInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoice>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceAssignInvoice().');
        }
        if (requestParameters['subCourierId'] == null && requestParameters['subCourierUserId'] == null) {
            throw new runtime.RequiredError('subCourierId', 'Either "subCourierId" or "subCourierUserId" must be supplied when calling warehouseInvoiceAssignInvoice().');
        }
        if (requestParameters['subCourierId'] != null && requestParameters['subCourierUserId'] != null) {
            throw new runtime.RequiredError('subCourierId', 'Supply "subCourierId" or "subCourierUserId", not both, when calling warehouseInvoiceAssignInvoice().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/assign`,
            method: 'PUT',
            headers: headerParameters,
            query: queryParameters,
            body: {
                invoiceId: requestParameters['invoiceId'],
                subCourierId: requestParameters['subCourierId'],
                subCourierUserId: requestParameters['subCourierUserId'],
            },
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WarehouseInvoiceFromJSON(jsonValue));
    }

    async warehouseInvoiceAssignInvoice(requestParameters: WarehouseInvoiceAssignInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoice> {
        const response = await this.warehouseInvoiceAssignInvoiceRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceGetInvoiceCreditRaw(requestParameters: WarehouseInvoiceGetInvoiceCreditRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoiceCredit>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceGetInvoiceCredit().');
        }

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/credit`,
            method: 'GET',
            headers: {},
            query: {},
        }, initOverrides);

        return new runtime.JSONApiResponse(response);
    }

    async warehouseInvoiceGetInvoiceCredit(requestParameters: WarehouseInvoiceGetInvoiceCreditRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoiceCredit> {
        const response = await this.warehouseInvoiceGetInvoiceCreditRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceApplyCreditRaw(requestParameters: WarehouseInvoiceApplyCreditRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoice>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceApplyCredit().');
        }
        if (requestParameters['amount'] == null) {
            throw new runtime.RequiredError('amount', 'Required parameter "amount" was null or undefined when calling warehouseInvoiceApplyCredit().');
        }

        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/apply-credit`,
            method: 'POST',
            headers: headerParameters,
            query: {},
            body: {
                amount: requestParameters['amount'],
            },
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WarehouseInvoiceFromJSON(jsonValue));
    }

    async warehouseInvoiceApplyCredit(requestParameters: WarehouseInvoiceApplyCreditRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoice> {
        const response = await this.warehouseInvoiceApplyCreditRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceSendInvoiceEmailRaw(requestParameters: WarehouseInvoiceSendInvoiceEmailRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<object>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceSendInvoiceEmail().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const body: any = {
            invoiceId: requestParameters['invoiceId'],
        };
        if (requestParameters['isResend'] != null) {
            body.isResend = requestParameters['isResend'];
        }

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/send`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    async warehouseInvoiceSendInvoiceEmail(requestParameters: WarehouseInvoiceSendInvoiceEmailRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<object> {
        const response = await this.warehouseInvoiceSendInvoiceEmailRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceGetMyInvoicesRaw(requestParameters: WarehouseInvoiceGetMyInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<Array<WarehouseInvoice>>> {
        if (requestParameters['subCourierId'] == null) {
            throw new runtime.RequiredError('subCourierId', 'Required parameter "subCourierId" was null or undefined when calling warehouseInvoiceGetMyInvoices().');
        }

        const queryParameters: any = {};
        if (requestParameters['filters'] != null) {
            queryParameters['filters'] = JSON.stringify(requestParameters['filters']);
        }

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/WarehouseInvoices/my-invoices/${encodeURIComponent(String(requestParameters['subCourierId']))}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => jsonValue.map(WarehouseInvoiceFromJSON));
    }

    async warehouseInvoiceGetMyInvoices(requestParameters: WarehouseInvoiceGetMyInvoicesRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<Array<WarehouseInvoice>> {
        const response = await this.warehouseInvoiceGetMyInvoicesRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceGetUnpaidInvoiceSummaryRaw(requestParameters: WarehouseInvoiceGetUnpaidInvoiceSummaryRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<UnpaidInvoiceSummary>> {
        if (requestParameters['subCourierId'] == null) {
            throw new runtime.RequiredError('subCourierId', 'Required parameter "subCourierId" was null or undefined when calling warehouseInvoiceGetUnpaidInvoiceSummary().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/WarehouseInvoices/unpaid-summary/${encodeURIComponent(String(requestParameters['subCourierId']))}`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse<any>(response);
    }

    async warehouseInvoiceGetUnpaidInvoiceSummary(requestParameters: WarehouseInvoiceGetUnpaidInvoiceSummaryRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<UnpaidInvoiceSummary> {
        const response = await this.warehouseInvoiceGetUnpaidInvoiceSummaryRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Remind owners of a warehouse's unpaid bills (OX WhatsApp line + email).
     * At most one reminder per owner per channel per day, shared with the cron.
     */
    async warehouseInvoiceSendUnpaidRemindersRaw(requestParameters: WarehouseInvoiceSendUnpaidRemindersRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoiceReminderResult>> {
        if (requestParameters['warehouseCourierId'] == null) {
            throw new runtime.RequiredError('warehouseCourierId', 'Required parameter "warehouseCourierId" was null or undefined when calling warehouseInvoiceSendUnpaidReminders().');
        }

        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/unpaid-reminders/${encodeURIComponent(String(requestParameters['warehouseCourierId']))}`,
            method: 'POST',
            headers: headerParameters,
            query: {},
            body: {
                dryRun: requestParameters['dryRun'] !== false,
            },
        }, initOverrides);

        return new runtime.JSONApiResponse(response);
    }

    async warehouseInvoiceSendUnpaidReminders(requestParameters: WarehouseInvoiceSendUnpaidRemindersRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoiceReminderResult> {
        const response = await this.warehouseInvoiceSendUnpaidRemindersRaw(requestParameters, initOverrides);
        return await response.value();
    }

    async warehouseInvoiceRegenerateInvoiceRaw(requestParameters: WarehouseInvoiceRegenerateInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoice>> {
        if (requestParameters['invoiceId'] == null) {
            throw new runtime.RequiredError('invoiceId', 'Required parameter "invoiceId" was null or undefined when calling warehouseInvoiceRegenerateInvoice().');
        }

        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/invoice/${encodeURIComponent(String(requestParameters['invoiceId']))}/regenerate`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: {
                invoiceId: requestParameters['invoiceId'],
                ...(requestParameters['scope'] != null ? { scope: requestParameters['scope'] } : {}),
            },
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => WarehouseInvoiceFromJSON(jsonValue));
    }

    async warehouseInvoiceRegenerateInvoice(requestParameters: WarehouseInvoiceRegenerateInvoiceRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoice> {
        const response = await this.warehouseInvoiceRegenerateInvoiceRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Audit (and optionally correct) invoices whose stored totals disagree with
     * their own line items. Defaults to a dry run.
     */
    async warehouseInvoiceRepairTotalsRaw(requestParameters: WarehouseInvoiceRepairTotalsRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<WarehouseInvoiceRepairTotalsResult>> {
        const queryParameters: any = {};
        const headerParameters: runtime.HTTPHeaders = {};
        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/WarehouseInvoices/repair-totals`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: {
                dryRun: requestParameters['dryRun'],
                warehouseCourierId: requestParameters['warehouseCourierId'],
                invoiceIds: requestParameters['invoiceIds'],
            },
        }, initOverrides);

        return new runtime.JSONApiResponse<WarehouseInvoiceRepairTotalsResult>(response);
    }

    async warehouseInvoiceRepairTotals(requestParameters: WarehouseInvoiceRepairTotalsRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<WarehouseInvoiceRepairTotalsResult> {
        const response = await this.warehouseInvoiceRepairTotalsRaw(requestParameters, initOverrides);
        return await response.value();
    }
}
